import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyDetailsAndDashboardViews, SurveyDetailsAndDashboardViewsDTO } from "./surveyDetailsAndDashboardViews";
/* eslint-disable import/export */
export abstract class DeleteDashboardViewRPC implements
  LeoRPC<DeleteDashboardViewRPC.Request, DeleteDashboardViewRPC.Response, DeleteDashboardViewRPC.Errors.Errors>
{
  abstract execute(
    request: DeleteDashboardViewRPC.Request
  ): Promise<LeoRPCResult<DeleteDashboardViewRPC.Response, DeleteDashboardViewRPC.Errors.Errors>>;
}

export namespace DeleteDashboardViewRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    surveyViewId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isDeleteDashboardViewRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly surveyViewId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    surveyViewId: LeoUUID
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.surveyViewId = surveyViewId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const surveyViewId = getLeoUuid(dto, "surveyViewId", InvalidRequestSurveyViewIdError);
      return new Request(
        projectId,
        surveyId,
        surveyViewId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          surveyViewId: this.surveyViewId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `DeleteDashboardViewRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',surveyViewId='${this.surveyViewId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViewsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isDeleteDashboardViewResponse: boolean = true;
  
    readonly surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViews;
  
  
    constructor(
    surveyDetailsAndDashboardViews: SurveyDetailsAndDashboardViews
    ) {
      this.surveyDetailsAndDashboardViews = surveyDetailsAndDashboardViews;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyDetailsAndDashboardViews = SurveyDetailsAndDashboardViews.fromDTO(getObject(dto, "surveyDetailsAndDashboardViews", InvalidResponseSurveyDetailsAndDashboardViewsError));
      return new Response(
        surveyDetailsAndDashboardViews
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyDetailsAndDashboardViews: this.surveyDetailsAndDashboardViews.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `DeleteDashboardView(surveyDetailsAndDashboardViews='${this.surveyDetailsAndDashboardViews}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSurveyViewId = "INVALID_SURVEY_VIEW_ID",
    MasterSurveyViewCannotBeDeleted = "MASTER_SURVEY_VIEW_CANNOT_BE_DELETED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `DeleteDashboardViewRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `DeleteDashboardViewRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `DeleteDashboardViewRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidSurveyViewIdDTO {
      code: string;
    }
    
    export class InvalidSurveyViewId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyViewId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyViewId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyViewIdDTO {
        const dto: InvalidSurveyViewIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyViewId.prototype.toString = function toString(): string {
      return `DeleteDashboardViewRPC.INVALID_SURVEY_VIEW_ID()`;
    };
    
    
    export interface MasterSurveyViewCannotBeDeletedDTO {
      code: string;
    }
    
    export class MasterSurveyViewCannotBeDeleted extends LeoRPCError<RPCError> {
      readonly isMasterSurveyViewCannotBeDeleted: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MasterSurveyViewCannotBeDeleted;
        super(code);
      }
    
    
      toDTO(): MasterSurveyViewCannotBeDeletedDTO {
        const dto: MasterSurveyViewCannotBeDeletedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MasterSurveyViewCannotBeDeleted.prototype.toString = function toString(): string {
      return `DeleteDashboardViewRPC.MASTER_SURVEY_VIEW_CANNOT_BE_DELETED()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | ProjectAlreadyArchived | InvalidSurveyViewId | MasterSurveyViewCannotBeDeleted;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | ProjectAlreadyArchivedDTO | InvalidSurveyViewIdDTO | MasterSurveyViewCannotBeDeletedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSurveyViewId:
          return new InvalidSurveyViewId();
        case RPCError.MasterSurveyViewCannotBeDeleted:
          return new MasterSurveyViewCannotBeDeleted();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends DeleteDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends DeleteDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestSurveyViewIdError extends DeleteDashboardViewRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyViewIdError";
  }
}

export class InvalidResponseSurveyDetailsAndDashboardViewsError extends DeleteDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyDetailsAndDashboardViewsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends DeleteDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends DeleteDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends DeleteDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidSurveyViewIdError extends DeleteDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyViewIdError";
  }
}

export class InvalidResponseMasterSurveyViewCannotBeDeletedError extends DeleteDashboardViewRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMasterSurveyViewCannotBeDeletedError";
  }
}

